import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useDealershipSettings } from '@/data/hooks/useDealershipSettings';
import { scoreDealPriority } from '@/lib/dealPriority';

export type PaceTone = 'good' | 'warn' | 'bad' | 'neutral';

export interface PaceChip {
  key: string;
  label: string;
  tone: PaceTone;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DEMO_TYPES = new Set(['walkaround', 'test_drive', 'quote_review']);

function expectedFractionThroughDay(): number {
  const now = new Date();
  const start = 9; // assume 9–7 work day
  const end = 19;
  const h = now.getHours() + now.getMinutes() / 60;
  if (h <= start) return 0;
  if (h >= end) return 1;
  return (h - start) / (end - start);
}

function workingDaysLeftThisMonth(): number {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  let count = 0;
  for (let d = now.getDate(); d <= last; d++) {
    const day = new Date(now.getFullYear(), now.getMonth(), d).getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return Math.max(count, 1);
}

/**
 * Derives the three "Pace Today" chips from goals + activity + deal data.
 */
export function useRepPace() {
  const { mode, userId } = useAuth();
  const { activities, deals } = useDealershipData();
  const { settings } = useDealershipSettings();

  const repId = mode === 'demo' ? 'rep-alex' : userId;

  return useMemo<PaceChip[]>(() => {
    if (!repId) return [];
    const todayStr = new Date().toISOString().slice(0, 10);
    const myActsToday = activities.filter(
      (a) => a.rep_id === repId && a.created_at.slice(0, 10) === todayStr,
    );
    const myDeals = deals.filter((d) => d.rep_id === repId && d.stage !== 'sold');

    // 1. Activities pace
    const dailyTarget = settings.min_daily_activities;
    const expected = Math.round(dailyTarget * expectedFractionThroughDay());
    const delta = myActsToday.length - expected;
    let actChip: PaceChip;
    if (dailyTarget === 0) {
      actChip = { key: 'acts', label: `${myActsToday.length} activities today`, tone: 'neutral' };
    } else if (delta >= 1) {
      actChip = { key: 'acts', label: `+${delta} ahead of pace`, tone: 'good' };
    } else if (delta === 0) {
      actChip = { key: 'acts', label: 'On pace', tone: 'warn' };
    } else {
      actChip = { key: 'acts', label: `${Math.abs(delta)} behind pace`, tone: 'bad' };
    }

    // 2. Demos to monthly goal (prorated)
    const monthDemos = activities.filter(
      (a) =>
        a.rep_id === repId &&
        DEMO_TYPES.has(a.activity_type) &&
        new Date(a.created_at).getMonth() === new Date().getMonth(),
    ).length;
    const demoTarget = settings.monthly_demo_target;
    const daysLeft = workingDaysLeftThisMonth();
    const remaining = Math.max(demoTarget - monthDemos, 0);
    const dailyDemoNeeded = demoTarget > 0 ? Math.ceil(remaining / daysLeft) : 0;
    let demoChip: PaceChip;
    if (demoTarget === 0) {
      demoChip = { key: 'demos', label: `${monthDemos} demos this month`, tone: 'neutral' };
    } else if (remaining === 0) {
      demoChip = { key: 'demos', label: 'Demo goal hit 🎯', tone: 'good' };
    } else {
      demoChip = {
        key: 'demos',
        label: `${dailyDemoNeeded} demo${dailyDemoNeeded === 1 ? '' : 's'} to hit pace`,
        tone: dailyDemoNeeded > 2 ? 'bad' : 'warn',
      };
    }

    // 3. Next follow-up due
    const now = Date.now();
    const staleHours = settings.stale_deal_hours || 72;
    const ranked = myDeals
      .map((d) => ({ d, r: scoreDealPriority(d, { staleHours, now }) }))
      .filter((x) => x.r.reason !== 'normal')
      .sort((a, b) => b.r.score - a.r.score);
    let nextChip: PaceChip;
    if (ranked.length === 0) {
      nextChip = { key: 'next', label: 'Caught up', tone: 'good' };
    } else {
      const top = ranked[0];
      const ageMs = now - new Date(top.d.last_activity_at).getTime();
      const overdueMs = ageMs - (staleHours / 2) * 60 * 60 * 1000;
      if (overdueMs > 0) {
        const h = Math.round(overdueMs / (60 * 60 * 1000));
        nextChip = { key: 'next', label: `${h}h overdue: ${top.d.customer_name.split(' ')[0]}`, tone: 'bad' };
      } else {
        const mins = Math.max(Math.round(-overdueMs / 60_000), 1);
        nextChip = {
          key: 'next',
          label: `Next due in ${mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h`}`,
          tone: 'warn',
        };
      }
    }

    return [actChip, demoChip, nextChip];
  }, [activities, deals, settings, repId]);
}